// Port of v3's app-wide `composables/useIsReadOnly.js`, retyped, rule preserved 1:1.
//
//
//
// Port of v3's app-wide `composables/useIsReadOnly.js`, retyped, rule preserved 1:1.
//
// "May this caller ACT, or only look?" — read off `userAccessInfo`. Account level is checked FIRST
// and short-circuits, so an account-level viewer stays read-only even where a workspace row would
// say otherwise. That ordering is load-bearing v3 behaviour.
//
// NOT the same question as a feature's own capability read. `creative-ai`'s
// `useCreativeAiAccess().canEditLabels` asks "may this user author LABELS" off the workspace's
// `workspace_permissions ?? account_permissions` role strings and lets the workspace role win —
// v3 used THIS composable, not that one, for its read-only surfaces.
//
// Lives here (module 07 owns the session/RBAC surface every feature reads) because v3 kept it
// app-wide and v4 now has two consumers: `creative-ai`'s recommendation strip and
// `impact-analysis`'s. Both reach it through this barrel; neither copies it.
import { computed, type ComputedRef } from 'vue'

import { useAccount } from './useAccount'
import { useWorkspace } from './useWorkspace'

const VIEWER = 'viewer'

/** `userAccessInfo.workspaces` is `unknown[]`, so every read is narrowed at runtime — v3 did the
 *  same (`'id' in ws`), because a row's `id` is sometimes a scalar and sometimes an array. */
/** `userAccessInfo.workspaces` is `unknown[]`, so every read is narrowed at runtime — v3 did the
 *  same (`'id' in ws`), because a row's `id` is sometimes a scalar and sometimes an array. */
function matchesWorkspace(row: unknown, workspaceId: number | string): boolean {
  if (!row || typeof row !== 'object' || !('id' in row)) return false
  const id = (row as { id?: unknown }).id
  if (Array.isArray(id)) {
    return id.includes(Number(workspaceId)) || id.includes(String(workspaceId))
  }
  return String(id) === String(workspaceId)
}

export interface UseViewerAccess {
  /** True when the caller may look but not act. Absence of any level record means NOT a viewer. */
  /** True when the caller may look but not act. Absence of any level record means NOT a viewer. */
  isViewer: ComputedRef<boolean>
}

export function useViewerAccess(): UseViewerAccess {
  const account = useAccount()
  const workspace = useWorkspace()

  const isViewer = computed(() => {
    if (account.accountLevel.value === VIEWER) return true

    const workspaceId = workspace.workspaceId.value ?? workspace.workspace.value?.id
    if (workspaceId === undefined || workspaceId === null) return false

    const row = (account.userAccessInfo.value.workspaces ?? []).find((candidate) =>
      matchesWorkspace(candidate, workspaceId),
    )
    if (!row || typeof row !== 'object' || !('level' in row)) return false
    return (row as { level?: unknown }).level === VIEWER
  })

  return { isViewer }
}
